import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
    Button,
    Center,
    HighlightedCard,
    Stack,
    Subtitle1,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import {
    useFillPathway,
    useFillPrescription,
} from '../../../../contexts/fill/use-fill-prescription';
import { usePathwayOverride } from '../../../../hooks/use-pathway-override';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import { useProgramTerms } from '../../../../hooks/use-program-terms';
import { PharmacyChangeModal } from '../../cabinet/provider-offers/PharmacyChangeModal';

export type SelectInsurancePathCardProps = {
    offer: PrescriptionOffer | undefined;
};

export const SelectInsurancePathCard = ({
    offer,
}: SelectInsurancePathCardProps) => {
    const { t } = useTranslation('lilly');
    const [opened, { toggle }] = useDisclosure();

    const { setPathway } = useFillPathway();
    const { drxId, medication } = useFillPrescription();
    const pathwayOverride = usePathwayOverride(medication);

    // If the copay assistance card specifically requires commercial insurance, then patients with Government insurance (e.g. Medicaid) _must_ use self-pay
    const insuranceTerms = useProgramTerms(medication, 'insurance');
    const govtSelfPayOnly =
        insuranceTerms?.savingsCard?.insuranceRequirement === 'ANY_COMMERCIAL';

    const useInsurance = useCallback(() => {
        if (!offer) {
            toggle();
        } else {
            setPathway({ pathway: 'insurance' });
        }
    }, [offer, setPathway, toggle]);

    const line1 =
        offer?.headlinePriceType === 'CopayAssistance'
            ? govtSelfPayOnly
                ? t('fill.selfPay.pathSelect.cards.insurance.line1Commercial')
                : t('fill.selfPay.pathSelect.cards.insurance.line1')
            : t('fill.selfPay.pathSelect.cards.insurance.line1NoCard');
    const line2 =
        !offer || offer.headlinePriceType === 'InsuredPriceAtPharmacy'
            ? t('fill.pathSelect.cards.line2NoPrice')
            : offer.headlinePriceType === 'Incentive' ||
                offer.headlinePriceType === 'CashAssistance'
              ? t('fill.pathSelect.cards.line2', {
                    price: offer?.patientCost,
                })
              : t('fill.pathSelect.cards.line2PriceOnly', {
                    price: offer?.patientCost,
                });

    return (
        <>
            <HighlightedCard
                fullWidthFooter
                pinFooter
                fullHeight
                data-testid="path-select-card-insurance"
                header={
                    <Center data-testid="path-select-card-insurance-title">
                        {t('fill.selfPay.pathSelect.cards.insurance.heading')}
                    </Center>
                }
                footer={
                    <Button
                        fullWidth
                        variant={
                            pathwayOverride?.favoredPathway === 'insurance'
                                ? 'filled'
                                : 'outline'
                        }
                        onClick={useInsurance}
                    >
                        {t('fill.selfPay.pathSelect.cards.insurance.button')}
                    </Button>
                }
            >
                <Stack gap="md">
                    <Subtitle2>{line1}</Subtitle2>
                    <Subtitle1>{line2}</Subtitle1>
                </Stack>
            </HighlightedCard>
            <PharmacyChangeModal
                drxId={drxId}
                isOpen={opened}
                toggle={toggle}
            />
        </>
    );
};
